import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ConfirmModal } from "../components/ConfirmModal";
import { CustomSelect } from "../components/CustomSelect";
import type { CustomSelectOption } from "../components/CustomSelect";
import { Shell } from "../components/Shell";
import { DEPARTAMENTOS, divipolaCodeFor, municipiosDe } from "../data/colombia";
import {
  ApiError,
  impersonate as impersonateApi,
  tenants as tenantsApi,
} from "../lib/api";
import type { NivelTenant, Tenant, TenantStatus, TenantUpdateBody } from "../lib/api";

const NIVEL_OPTS: ReadonlyArray<{ value: NivelTenant; label: string }> = [
  { value: "nacional", label: "Gobierno Nacional" },
  { value: "regional", label: "Regional" },
  { value: "gobernacion", label: "Gobernación" },
  { value: "municipio", label: "Municipio / Alcaldía" },
  { value: "jac", label: "Junta de Acción Comunal" },
];

const STATUS_LABEL: Record<TenantStatus, string> = {
  trial: "En prueba",
  active: "Activa",
  suspended: "Suspendida",
  cancelled: "Cancelada",
};

const STATUS_COLOR: Record<TenantStatus, string> = {
  trial: "var(--warn)",
  active: "var(--ok)",
  suspended: "var(--err)",
  cancelled: "var(--ink-4)",
};

export function TenantDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [tenant, setTenant] = useState<Tenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ kind: "ok" | "err"; msg: string } | null>(null);
  const [editOpen, setEditOpen] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [pendingStatusChange, setPendingStatusChange] = useState<{
    next: TenantStatus;
    title: string;
    message: string;
  } | null>(null);
  const reload = async () => {
    if (!id) return;
    setLoading(true);
    try {
      const t = await tenantsApi.get(id);
      setTenant(t);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : "Error de red");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const flash = (kind: "ok" | "err", msg: string) => {
    setToast({ kind, msg });
    setTimeout(() => setToast(null), 3000);
  };

  const onSoporte = async () => {
    if (!id) return;
    setBusy("soporte");
    try {
      const r = await impersonateApi.startSupport(id);
      window.open(r.redirect_url, "_blank", "noopener");
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "No se pudo generar token");
    } finally {
      setBusy(null);
    }
  };

  const changeStatus = (next: TenantStatus, confirmMsg?: string) => {
    if (!id || !tenant) return;
    if (confirmMsg) {
      const title = `¿Cambiar estado a ${STATUS_LABEL[next]}?`;
      setPendingStatusChange({ next, title, message: confirmMsg });
      return;
    }
    void doChangeStatus(next);
  };

  const doChangeStatus = async (next: TenantStatus) => {
    if (!id) return;
    setBusy("status");
    try {
      const updated = await tenantsApi.update(id, { status: next });
      setTenant(updated);
      flash("ok", `Estado cambiado a ${STATUS_LABEL[next]}`);
      setPendingStatusChange(null);
    } catch (err) {
      flash("err", err instanceof ApiError ? err.message : "No se pudo cambiar");
    } finally {
      setBusy(null);
    }
  };

  if (loading) {
    return (
      <Shell>
        <section className="page">
          <p style={{ color: "var(--ink-3)" }}>Cargando alcaldía…</p>
        </section>
      </Shell>
    );
  }

  if (error || !tenant) {
    return (
      <Shell>
        <section className="page">
          <div className="alert err">{error ?? "No se pudo cargar"}</div>
          <p>
            <Link to="/tenants">← Volver a alcaldías</Link>
          </p>
        </section>
      </Shell>
    );
  }

  return (
    <Shell>
      <section className="page">
        <div className="eyebrow">
          <Link to="/tenants">← Alcaldías</Link>
          {" · "}
          <span>{tenant.slug}</span>
        </div>
        <h1 style={{ marginTop: 8 }}>{tenant.nombre}</h1>
        <p className="lede">
          <span style={{ color: "var(--ink-3)" }}>NIT</span>{" "}
          <strong style={{ fontFamily: "var(--font-mono)" }}>{tenant.nit}</strong>{" · "}
          {tenant.municipio}, {tenant.departamento}{" · "}
          estado{" "}
          <strong style={{ color: STATUS_COLOR[tenant.status] }}>
            {STATUS_LABEL[tenant.status]}
          </strong>
        </p>

        <div className="tenant-sections">
          {/* Estado */}
          <article className="tenant-section-card" style={{ cursor: "default" }}>
            <SectionHeader
              subtitle="Ciclo de vida"
              label="Estado del tenant"
              badge={STATUS_LABEL[tenant.status]}
              badgeColor={STATUS_COLOR[tenant.status]}
            />
            <p className="tenant-section-desc">
              {tenant.status === "trial"
                ? "En periodo de prueba. Actívalo cuando el cliente firme el contrato."
                : tenant.status === "active"
                  ? "Tenant operando con todos los canales habilitados."
                  : tenant.status === "suspended"
                    ? "Login bloqueado para todo el equipo. Los datos siguen ahí."
                    : "Cancelada. Sin acceso ni operación."}
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {tenant.status === "trial" && (
                <button
                  type="button"
                  className="tenant-status-btn ok"
                  disabled={busy === "status"}
                  onClick={() => void changeStatus("active")}
                >
                  Activar
                </button>
              )}
              {tenant.status === "active" && (
                <button
                  type="button"
                  className="tenant-status-btn warn"
                  disabled={busy === "status"}
                  onClick={() =>
                    void changeStatus(
                      "suspended",
                      `¿Suspender "${tenant.nombre}"? Los funcionarios no podrán entrar.`,
                    )
                  }
                >
                  Suspender
                </button>
              )}
              {tenant.status === "suspended" && (
                <button
                  type="button"
                  className="tenant-status-btn ok"
                  disabled={busy === "status"}
                  onClick={() => void changeStatus("active")}
                >
                  Reactivar
                </button>
              )}
              {tenant.status !== "cancelled" && (
                <button
                  type="button"
                  className="tenant-status-btn danger"
                  disabled={busy === "status"}
                  onClick={() =>
                    void changeStatus(
                      "cancelled",
                      `¿Cancelar "${tenant.nombre}" definitivamente? Esta operación deja la alcaldía sin acceso.`,
                    )
                  }
                >
                  Cancelar
                </button>
              )}
            </div>
          </article>

          {/* Editar datos */}
          <button
            type="button"
            className="tenant-section-card tenant-section-card-btn"
            onClick={() => setEditOpen(true)}
          >
            <SectionHeader subtitle="Datos básicos" label="Editar alcaldía" />
            <p className="tenant-section-desc">
              Actualiza nombre, contacto, departamento/municipio, plan, código DIVIPOLA y
              teléfono. El NIT y el slug no se pueden cambiar.
            </p>
            <span className="tenant-section-cta">Abrir →</span>
          </button>

          {/* Soporte */}
          <button
            type="button"
            className="tenant-section-card tenant-section-card-btn"
            disabled={busy === "soporte"}
            onClick={() => void onSoporte()}
          >
            <SectionHeader
              subtitle="Impersonar — solo lectura"
              label="Sesión de soporte"
              badge="Token efímero"
              badgeKind="warn"
            />
            <p className="tenant-section-desc">
              Abre una sesión temporal (15 min) en la SPA del tenant para diagnosticar
              problemas sin tocar datos. Toda la actividad queda registrada en auditoría.
            </p>
            <span className="tenant-section-cta">Abrir ↗</span>
          </button>

          {/* Funcionarios */}
          <Link
            to={`/tenants/${tenant.id}/funcionarios`}
            className="tenant-section-card tenant-section-card-link"
          >
            <SectionHeader subtitle="Equipo del tenant" label="Funcionarios" />
            <p className="tenant-section-desc">
              Crea, edita o desactiva funcionarios de la alcaldía. Asigna roles
              (admin_tenant, supervisor, funcionario, radicador) y dependencias.
            </p>
            <span className="tenant-section-cta">Abrir →</span>
          </Link>

          {/* Canales (incluye WhatsApp) */}
          <Link
            to={`/tenants/${tenant.id}/canales`}
            className="tenant-section-card tenant-section-card-link"
          >
            <SectionHeader
              subtitle="WhatsApp · Telegram · Email · Messenger · Instagram · Webchat"
              label="Canales digitales"
            />
            <p className="tenant-section-desc">
              Activa y configura todos los canales por donde la alcaldía recibe y responde
              PQRSDF. Incluye WhatsApp Business (Meta Cloud API) y el resto de canales
              auto-gestionados.
            </p>
            <span className="tenant-section-cta">Abrir →</span>
          </Link>

          {/* Identidad institucional (logo, escudo, firma, color) */}
          <Link
            to={`/tenants/${tenant.id}/branding`}
            className="tenant-section-card tenant-section-card-link"
          >
            <SectionHeader
              subtitle="Logo · escudo · firma · color"
              label="Identidad institucional"
            />
            <p className="tenant-section-desc">
              Carga el logo y escudo de la alcaldía, define el color
              institucional y los datos de firma que aparecen en el
              membrete del PDF oficial de cada respuesta.
            </p>
            <span className="tenant-section-cta">Abrir →</span>
          </Link>

          {/* Hub central de servicios activables (Turnos, GD Actos,
              GD Correspondencia, Bot RAG, AI Draft, Firma PAdES). */}
          <Link
            to={`/tenants/${tenant.id}/servicios`}
            className="tenant-section-card tenant-section-card-link"
          >
            <SectionHeader
              subtitle="Turnos · GD · IA · Firma"
              label="Servicios"
              badge="Activar / desactivar"
            />
            <p className="tenant-section-desc">
              Hub central para activar o desactivar todos los servicios
              de la alcaldía: turnos, gestión documental (actos y
              correspondencia), bot RAG del chat, borrador IA y firma
              electrónica. Cada servicio enlaza a su configuración
              detallada.
            </p>
            <span className="tenant-section-cta">Abrir →</span>
          </Link>

          {/* Asistente IA · Borrador + Jarvis (WhatsApp) */}
          <Link
            to={`/tenants/${tenant.id}/ai-draft`}
            className="tenant-section-card tenant-section-card-link"
          >
            <SectionHeader
              subtitle="Proveedor IA · Borrador · WhatsApp"
              label="Asistente IA · Jarvis"
            />
            <p className="tenant-section-desc">
              Elige el proveedor y modelo de IA (OpenAI / Claude / …) y el
              consentimiento de datos. Aquí también activas el asistente por
              WhatsApp (Jarvis) para los funcionarios verificados de esta
              alcaldía.
            </p>
            <span className="tenant-section-cta">Abrir →</span>
          </Link>

          {/* Encuestas de satisfacción */}
          <Link
            to={`/tenants/${tenant.id}/encuestas`}
            className="tenant-section-card tenant-section-card-link"
          >
            <SectionHeader
              subtitle="Preguntas · métricas · respuestas"
              label="Encuestas"
            />
            <p className="tenant-section-desc">
              Configura las preguntas extra de la encuesta de satisfacción
              que recibe el ciudadano tras cada respuesta. Revisa métricas
              agregadas (rating promedio, NPS, % resolvió) y comentarios
              recientes con permalink al radicado.
            </p>
            <span className="tenant-section-cta">Abrir →</span>
          </Link>

          {/* SLA */}
          <Link
            to={`/tenants/${tenant.id}/sla`}
            className="tenant-section-card tenant-section-card-link"
          >
            <SectionHeader
              subtitle="Días hábiles · tipo × prioridad"
              label="SLA"
            />
            <p className="tenant-section-desc">
              Define los plazos internos de respuesta a PQRSDF de la
              alcaldía. Cada combinación tipo × prioridad. Nunca puede
              exceder el máximo legal de Ley 1755/2015.
            </p>
            <span className="tenant-section-cta">Abrir →</span>
          </Link>

        </div>
      </section>

      {editOpen && (
        <EditTenantModal
          tenant={tenant}
          onClose={() => setEditOpen(false)}
          onSaved={(t) => {
            setTenant(t);
            setEditOpen(false);
            flash("ok", "Datos guardados");
          }}
          onError={(m) => flash("err", m)}
        />
      )}

      <ConfirmModal
        open={pendingStatusChange !== null}
        title={pendingStatusChange?.title ?? ""}
        message={pendingStatusChange?.message}
        confirmLabel="Sí, continuar"
        variant={
          pendingStatusChange?.next === "suspended" ||
          pendingStatusChange?.next === "cancelled"
            ? "danger"
            : "default"
        }
        busy={busy === "status"}
        onCancel={() => setPendingStatusChange(null)}
        onConfirm={() => {
          if (pendingStatusChange) {
            void doChangeStatus(pendingStatusChange.next);
          }
        }}
      />

      {toast && <div className={`toast ${toast.kind}`}>{toast.msg}</div>}
    </Shell>
  );
}

function SectionHeader({
  subtitle,
  label,
  badge,
  badgeKind,
  badgeColor,
}: {
  subtitle: string;
  label: string;
  badge?: string;
  badgeKind?: "warn" | "ok" | "info";
  badgeColor?: string;
}) {
  const bg =
    badgeColor
      ? "rgba(20,22,26,0.06)"
      : badgeKind === "warn"
        ? "rgba(182,133,42,0.14)"
        : badgeKind === "ok"
          ? "rgba(30,122,77,0.14)"
          : "rgba(42,91,140,0.14)";
  const color =
    badgeColor ??
    (badgeKind === "warn"
      ? "var(--warn)"
      : badgeKind === "ok"
        ? "var(--ok)"
        : "var(--info)");
  return (
    <header
      style={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "flex-start",
        gap: 12,
        marginBottom: 10,
      }}
    >
      <div style={{ minWidth: 0 }}>
        <div
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 10.5,
            letterSpacing: "0.16em",
            textTransform: "uppercase",
            color: "var(--ink-3)",
            fontWeight: 600,
            marginBottom: 4,
          }}
        >
          {subtitle}
        </div>
        <h2
          style={{
            fontFamily: "var(--font-display)",
            fontSize: 20,
            letterSpacing: "-0.012em",
            margin: 0,
            color: "var(--ink)",
          }}
        >
          {label}
        </h2>
      </div>
      {badge && (
        <span
          style={{
            fontFamily: "var(--font-mono)",
            fontSize: 9.5,
            letterSpacing: "0.14em",
            textTransform: "uppercase",
            fontWeight: 600,
            padding: "3px 8px",
            borderRadius: 6,
            background: bg,
            color,
            whiteSpace: "nowrap",
            flexShrink: 0,
          }}
        >
          {badge}
        </span>
      )}
    </header>
  );
}

function EditTenantModal({
  tenant,
  onClose,
  onSaved,
  onError,
}: {
  tenant: Tenant;
  onClose: () => void;
  onSaved: (t: Tenant) => void;
  onError: (m: string) => void;
}) {
  const [nombre, setNombre] = useState(tenant.nombre);
  const [departamento, setDepartamento] = useState(tenant.departamento);
  const [municipio, setMunicipio] = useState(tenant.municipio);
  const [emailContacto, setEmailContacto] = useState(tenant.email_contacto ?? "");
  const [telefono, setTelefono] = useState(tenant.telefono ?? "");
  const [nivel, setNivel] = useState<NivelTenant>(tenant.nivel);
  const [parentId, setParentId] = useState<string>(tenant.parent_id ?? "");
  const [derivacion, setDerivacion] = useState(tenant.derivacion_habilitada);
  const [aprobacion, setAprobacion] = useState(tenant.aprobacion_habilitada);
  const [busy, setBusy] = useState(false);
  const [tenants, setTenants] = useState<Tenant[]>([]);

  useEffect(() => {
    tenantsApi
      .list({})
      .then((r) => setTenants(r.items))
      .catch(() => setTenants([]));
  }, []);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  const depOpts: ReadonlyArray<CustomSelectOption<string>> = DEPARTAMENTOS.map((d) => ({
    value: d,
    label: d,
  }));

  const munisDelDepto = municipiosDe(departamento);
  const muniOpts: ReadonlyArray<CustomSelectOption<string>> = munisDelDepto.map((m) => ({
    value: m,
    label: m,
  }));

  // Si el municipio actual ya no pertenece al nuevo departamento, resetea
  useEffect(() => {
    if (departamento && !munisDelDepto.includes(municipio)) {
      setMunicipio(munisDelDepto[0] ?? "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [departamento]);

  const submit = async () => {
    setBusy(true);
    try {
      const autoCode = divipolaCodeFor(departamento, municipio);
      const body: TenantUpdateBody = {
        nombre: nombre.trim(),
        departamento: departamento.trim(),
        municipio: municipio.trim(),
        email_contacto: emailContacto.trim() || undefined,
        telefono: telefono.trim() || undefined,
        codigo_divipola: autoCode ?? undefined,
        nivel,
        ...(parentId ? { parent_id: parentId } : { set_root: true }),
        derivacion_habilitada: derivacion,
        aprobacion_habilitada: aprobacion,
      };
      const updated = await tenantsApi.update(tenant.id, body);
      onSaved(updated);
    } catch (err) {
      onError(err instanceof ApiError ? err.message : "Error de red");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="modal-backdrop"
      role="dialog"
      aria-modal="true"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="modal-panel">
        <div className="modal-inner">
          <header className="modal-head">
            <div className="ttl">
              <span className="eyebrow">Datos básicos</span>
              <h2>Editar {tenant.nombre}</h2>
            </div>
            <button
              type="button"
              className="modal-close"
              aria-label="Cerrar"
              onClick={onClose}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <line x1="18" y1="6" x2="6" y2="18" strokeLinecap="round" />
                <line x1="6" y1="6" x2="18" y2="18" strokeLinecap="round" />
              </svg>
            </button>
          </header>
          <div className="modal-body">
            <div className="form-row">
              <label className="label">Nombre oficial</label>
              <input className="input" value={nombre} onChange={(e) => setNombre(e.target.value)} />
            </div>
            <div className="channel-form-grid">
              <div className="form-row">
                <label className="label">Departamento</label>
                <CustomSelect
                  value={departamento}
                  onChange={(v) => setDepartamento(v)}
                  options={depOpts}
                  aria-label="Departamento"
                />
              </div>
              <div className="form-row">
                <label className="label">Municipio</label>
                <CustomSelect
                  value={municipio}
                  onChange={(v) => setMunicipio(v)}
                  options={muniOpts}
                  disabled={!departamento || muniOpts.length === 0}
                  aria-label="Municipio"
                />
              </div>
            </div>
            <div className="channel-form-grid">
              <div className="form-row">
                <label className="label">Email de contacto</label>
                <input
                  className="input"
                  type="email"
                  value={emailContacto}
                  onChange={(e) => setEmailContacto(e.target.value)}
                />
              </div>
              <div className="form-row">
                <label className="label">Teléfono</label>
                <input
                  className="input"
                  value={telefono}
                  onChange={(e) => setTelefono(e.target.value)}
                />
              </div>
            </div>
            <div className="channel-form-grid">
              <div className="form-row">
                <label className="label">Código DIVIPOLA</label>
                <div
                  style={{
                    background: "var(--bone)",
                    border: "1px solid var(--hairline)",
                    padding: "12px 14px",
                    fontFamily: "var(--font-mono)",
                    fontSize: 15,
                    color: divipolaCodeFor(departamento, municipio)
                      ? "var(--ink)"
                      : "var(--ink-4)",
                    minHeight: 46,
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {divipolaCodeFor(departamento, municipio) ??
                    "—  (depto sin tabla cargada)"}
                </div>
                <p className="helper">
                  Código DANE derivado automáticamente del par
                  departamento + municipio. Se usa en reportes MIPG e
                  interoperabilidad con sistemas del Estado.
                </p>
              </div>
            </div>

            <div className="channel-form-grid">
              <div className="form-row">
                <label className="label">Nivel jerárquico</label>
                <CustomSelect
                  value={nivel}
                  onChange={(v) => setNivel(v as NivelTenant)}
                  options={NIVEL_OPTS.map((o) => ({ value: o.value, label: o.label }))}
                  aria-label="Nivel jerárquico"
                />
              </div>
              <div className="form-row">
                <label className="label">Entidad superior (padre)</label>
                <CustomSelect
                  value={parentId}
                  onChange={(v) => setParentId(v)}
                  options={[
                    { value: "", label: "— Sin padre (raíz) —" },
                    ...tenants
                      .filter((t) => t.id !== tenant.id)
                      .map((t) => ({ value: t.id, label: t.nombre })),
                  ]}
                  aria-label="Entidad superior"
                />
                <p className="helper">
                  El nivel superior verá los datos de este tenant (solo lectura).
                </p>
              </div>
            </div>

            <div className="form-row">
              <label
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                <input
                  type="checkbox"
                  checked={derivacion}
                  onChange={(e) => setDerivacion(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  Habilitar <strong>derivación por competencia</strong> — permite a los
                  funcionarios de este tenant derivar un radicado a su nivel superior. Si está
                  desactivado, la opción queda oculta (no saben que existe la jerarquía).
                </span>
              </label>
            </div>

            <div className="form-row">
              <label
                style={{
                  display: "flex",
                  gap: 10,
                  alignItems: "flex-start",
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                <input
                  type="checkbox"
                  checked={aprobacion}
                  onChange={(e) => setAprobacion(e.target.checked)}
                  style={{ marginTop: 3 }}
                />
                <span>
                  Exigir <strong>aprobación de coordinación</strong> — los funcionarios envían la
                  respuesta a revisión y la coordinación (permiso <code>pqrsdf.approve</code>) la
                  aprueba o devuelve antes de que salga al ciudadano. Desactivado: responden
                  directo. La alcaldía puede afinar en su organigrama qué dependencias lo requieren
                  (si no marca ninguna, aplica a toda la alcaldía).
                </span>
              </label>
            </div>

            <p className="helper" style={{ marginTop: 12 }}>
              <strong>NIT</strong>{" "}
              <span style={{ fontFamily: "var(--font-mono)" }}>{tenant.nit}</span>{" "}
              y <strong>slug</strong>{" "}
              <span style={{ fontFamily: "var(--font-mono)" }}>{tenant.slug}</span>{" "}
              no se pueden cambiar (afectan subdominio, facturación y links públicos).
            </p>

            <div style={{ display: "flex", gap: 10, marginTop: 20 }}>
              <button type="button" className="btn-outline" onClick={onClose}>
                Cancelar
              </button>
              <button
                type="button"
                className="cta"
                style={{ width: "auto", flex: 1 }}
                disabled={busy || !nombre.trim()}
                onClick={() => void submit()}
              >
                <span className="cta-inner">
                  {busy && <span className="spinner" aria-hidden />}
                  {busy ? "Guardando…" : "Guardar cambios"}
                </span>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
